/**
 * This is a module which represents a sample treatment.
 *
 * It's a great starting point, which will help you:
 * - signal "experiment.triggered" events in a consistent way for the "control" cohort
 * - express targeting/triggers in a single module
 * - express treatment in a single module
 * - express requirements in a single module
 *
 * If it makes sense to refactor/rename the methods, then please do so!
 */

const {doForCohortsNamed, CONTROL} = require('common-core/treatment-utils');
const {Waiter, WaiterTimeoutException, WaiterCancelException} = require('common-core/waiter');

// Use this to record any state which may be passed in through init(), and used
// while preparing or applying the treatment.
const state = {};

/**
 * A great place to setup a trigger, or do preparation
 * that is helpful to occur much earlier than when applying treatment.
 *
 * This should be used for changes that must be applied immediately and synchronously,
 * such as appending stylesheets.
 *
 * This could also be used to setup any analytics on existing features that should be
 * applied in both control and variations.
 *
 * If you need to do something for a specific variation, use doForCohortsNamed.
 */
function prepareTreatment() {
    doForCohortsNamed(CONTROL, () => {
    });
}

/**
 * Fire an "experiment.triggered.*" event.
 *
 * Signifies that the user has met the appropriate requirements,
 * that would have, or will apply treatment.
 *
 * You should invoke this any time a user has a different experience than
 * in the "control" variation.
 *
 * Name this appropriately for your treatment!
 */
function wouldSeeTreatment() {
    require("event!?experiment.triggered.reset.adg3.dismissed.property")();
}

/**
 * Apply some kind of treatment.
 *
 * This might be a good place to call wouldSeeTreatment().
 *
 * If you need to do something for a specific variation, use doForCohortsNamed.
 */
function applyTreatment() {
    wouldSeeTreatment();

    doForCohortsNamed(CONTROL, () => {
        var isInAdg2 = document.querySelector('body.adg3') === null;
        if (!isInAdg2) {
            return;
        }

        var contextPath = AJS.Meta.get('context-path');
        var username = AJS.Meta.get('remote-user');
        AJS.$.ajax({
            type: 'GET',
            url: contextPath + '/rest/api/2/user/properties/reset-adg3-dismissed-state?username=' + username,
            dataType: 'json',
            contentType: 'application/json'
        }).done(function() {
            require("event!?experiment.was.already.done")();
        }).fail(function(response) {
            require("event!?experiment.getting.is.reset.adg3.dismissed.state.failed")();
            var notExistString = 'The property reset-adg3-dismissed-state does not exist.';
            if (response.responseText.indexOf(notExistString) !== -1) {
                resetAdg3DismissedState();
            }
        });

        function resetAdg3DismissedState() {
            require("event!?experiment.reset.adg3.dismissed.state.will.do")();
            AJS.$.ajax({
                type: 'POST',
                url: contextPath + '/rest/api/2/userProperties',
                dataType: 'json',
                contentType: 'application/json',
                data: JSON.stringify({
                    key: 'atl.jira.simplifyux.rejected',
                    value: 'false'
                })
            }).done(function() {
                require("event!?experiment.reset.adg3.dismissed.state.done")();
                setExperimentDone();
            }).fail(function() {
                require("event!?experiment.reset.adg3.dismissed.state.failed")();
            })
        }

        function setExperimentDone() {
            AJS.$.ajax({
                type: 'PUT',
                processData: false,
                url: contextPath + '/rest/api/2/user/properties/reset-adg3-dismissed-state?username=' + username,
                dataType: 'json',
                contentType: 'application/json',
                data: 'true'
            }).done(function() {
                require("event!?experiment.set.experiment.done")();
            });
        }
    });
}

module.exports = {
    /**
     * Initialize the (potential) treatment.
     *
     * This is an opportunity to setup triggers.
     *
     * Do not include/write code that applies treatment in this function.
     * Instead, do this in another function (e.g. prepareTreatment, applyTreatment)
     * and invoke this function instead.
     */
    init(args1, args2) {
        // Store any necessary state data
        state['prop1'] = args1;
        state['prop2'] = args2;

        prepareTreatment();

        // Determine when to apply the treatment. This can be done by, e.g.
        // * Waiting for a specific element to appear
        // * Waiting for a particular event to be fired, or similar.

        // Wait for a specific element to be available before applying the treatment.
        // Change selector as needed.
        (new Waiter("body")).wait()
            .then(applyTreatment)
            .catch((e) => {
                if (e instanceof WaiterTimeoutException) {
                    // Timed out.
                } else if (e instanceof WaiterCancelException) {
                    // Cancelled
                } else {
                    // Something unexpected happened.
                    throw e;
                }
            });
    }
};



// WEBPACK FOOTER //
// ./src/treatment/sample/sample-treatment.js